import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useUser } from '../contexts/UserContext';

const emptyFormData = {
  name: '',
  email: '',
  phone: '',
  address_street: '',
  address_street2: '',
  address_city: '',
  address_state: '',
  address_zip: '',
  notes: '',
};

function Clients() {
  const { canCreateData } = useUser();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const data = await api.getClients();
      setClients(data);
    } catch (err) {
      setError(err.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter((client) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      client.name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.phone?.toLowerCase().includes(query)
    );
  });

  const openAddModal = () => {
    if (!canCreateData) return; // Guard: trial expired
    setEditingClient(null);
    setFormData(emptyFormData);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (e, client) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canCreateData) return; // Guard: trial expired
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      email: client.email || '',
      phone: client.phone || '',
      address_street: client.address_street || '',
      address_street2: client.address_street2 || '',
      address_city: client.address_city || '',
      address_state: client.address_state || '',
      address_zip: client.address_zip || '',
      notes: client.notes || '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreateData) return; // Guard: trial expired
    setFormError('');

    if (!formData.name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setFormError('Email is required');
      return;
    }

    setSaving(true);
    try {
      if (editingClient) {
        await api.updateClient(editingClient.id, formData);
      } else {
        await api.createClient(formData);
      }
      setShowModal(false);
      fetchClients();
    } catch (err) {
      setFormError(err.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e, client) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete ${client.name}?`)) {
      return;
    }

    try {
      await api.deleteClient(client.id);
      fetchClients();
    } catch (err) {
      alert(err.message || 'Failed to delete client');
    }
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Clients</h1>
            <p className="text-slate-600">Manage your clients and contacts</p>
          </div>
          <button
            onClick={openAddModal}
            disabled={!canCreateData}
            className={`inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-bold transition shadow-lg ${
              canCreateData
                ? 'bg-secondary text-white hover:bg-orange-600'
                : 'bg-slate-300 text-slate-500 cursor-not-allowed'
            }`}
            title={!canCreateData ? 'Upgrade to Pro to add clients' : ''}
          >
            <i className={`fas ${canCreateData ? 'fa-plus' : 'fa-lock'}`}></i>
            <span>Add Client</span>
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Client List */}
        {filteredClients.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center">
            <i className="fas fa-users text-6xl text-slate-300 mb-4"></i>
            <h3 className="text-xl font-bold text-slate-700 mb-2">
              {clients.length === 0 ? 'No Clients Yet' : 'No Results Found'}
            </h3>
            <p className="text-slate-600 mb-6">
              {clients.length === 0
                ? 'Add your first client to get started.'
                : 'Try adjusting your search.'}
            </p>
            {clients.length === 0 && canCreateData && (
              <button
                onClick={openAddModal}
                className="bg-secondary text-white px-6 py-3 rounded-xl font-bold hover:bg-orange-600 transition"
              >
                Add Client
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredClients.map((client) => (
              <Link
                key={client.id}
                to={`/clients/${client.id}`}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md hover:border-secondary/30 transition block"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-primary mb-1">{client.name}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                      <span>
                        <i className="fas fa-envelope mr-2 text-slate-400"></i>
                        {client.email}
                      </span>
                      {client.phone && (
                        <span>
                          <i className="fas fa-phone mr-2 text-slate-400"></i>
                          {client.phone}
                        </span>
                      )}
                      {client.address_city && (
                        <span>
                          <i className="fas fa-map-marker-alt mr-2 text-slate-400"></i>
                          {client.address_city}{client.address_state && `, ${client.address_state}`}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {canCreateData ? (
                      <>
                        <button
                          onClick={(e) => openEditModal(e, client)}
                          className="px-4 py-2 text-slate-600 hover:text-primary hover:bg-slate-100 rounded-lg transition"
                        >
                          <i className="fas fa-edit mr-1"></i> Edit
                        </button>
                        <button
                          onClick={(e) => handleDelete(e, client)}
                          className="px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                        >
                          <i className="fas fa-trash mr-1"></i> Delete
                        </button>
                      </>
                    ) : (
                      <span className="px-3 py-1 text-xs text-slate-400 bg-slate-100 rounded-lg">
                        <i className="fas fa-lock mr-1"></i> View Only
                      </span>
                    )}
                    <i className="fas fa-chevron-right text-slate-400 ml-2"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Summary */}
        {filteredClients.length > 0 && (
          <div className="mt-6 text-center text-slate-600">
            Showing {filteredClients.length} of {clients.length} clients
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-primary">
                    {editingClient ? 'Edit Client' : 'Add Client'}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-slate-400 hover:text-slate-600 text-2xl"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                  {formError && (
                    <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                      {formError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  {/* Address Section */}
                  <div className="pt-2 border-t border-slate-200">
                    <p className="text-sm font-bold text-slate-700 mb-3">Address</p>

                    <div className="space-y-3">
                      <input
                        type="text"
                        value={formData.address_street}
                        onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                        placeholder="Street address"
                      />
                      <input
                        type="text"
                        value={formData.address_street2}
                        onChange={(e) => setFormData({ ...formData, address_street2: e.target.value })}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                        placeholder="Apt, suite, unit, etc. (optional)"
                      />
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={formData.address_city}
                          onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                          placeholder="City"
                        />
                        <input
                          type="text"
                          value={formData.address_state}
                          onChange={(e) => setFormData({ ...formData, address_state: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                          placeholder="State"
                        />
                        <input
                          type="text"
                          value={formData.address_zip}
                          onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
                          className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
                          placeholder="ZIP"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition resize-none"
                      placeholder="Internal notes about this client..."
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="flex-1 px-6 py-3 border-2 border-slate-200 text-slate-700 rounded-xl font-bold hover:bg-slate-50 transition"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 px-6 py-3 bg-secondary text-white rounded-xl font-bold hover:bg-orange-600 transition disabled:opacity-50"
                    >
                      {saving ? 'Saving...' : editingClient ? 'Update' : 'Add Client'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Clients;
